'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useState } from 'react';
import { PrizePoolContext, type PrizePoolContextValue, type PrizePoolStage } from './prize-pool-context';
export interface PrizePoolProviderProps {
  amount: number;
  viewerStatePending: boolean;
  locked: boolean;
  isWinner: boolean;
  isLoser: boolean;
  userShareD6: string | null;
  userSharePct: number;
  payoutEstimates: PrizePoolContextValue['state']['payoutEstimates'];
  payoutEstimatesPending: boolean;
  /** Label of the winning outcome — feeds the finalized-loser closure chip. */
  winningOutcomeLabel: string | null;
  /** Replaces the legacy `showClaimed` boolean. */
  claimed: boolean;
  /** True while a claim transaction is in flight (was `isClaimLoading`). */
  claimInFlight?: boolean;
  onClaim: () => void;
  children: ReactNode;
}
function deriveStage(viewerStatePending: boolean, locked: boolean, isWinner: boolean): PrizePoolStage {
  if (locked) {
    if (viewerStatePending) return 'loading';
    if (isWinner) return 'finalized-winner';
    // Loser AND non-holder collapse together — same card copy.
    return 'finalized-loser';
  }
  return 'pre-finalization';
}

/**
 * Owns prize-pool state for one or more `<PrizePool.*>` subtrees. The provider
 * derives the simplified {@link PrizePoolStage} discriminated union from the
 * boolean inputs (`locked`, `isWinner`, `isLoser`) so the
 * card reads a single state field instead of branching on multiple flags.
 *
 * Mount once per logical surface (e.g. one per market detail page wrapping
 * both mobile and desktop subtrees) so all `<PrizePool.HelpButton>` instances
 * share the same modal state.
 */
export function PrizePoolProvider(t0) {
  const $ = _c(24);
  const {
    amount,
    viewerStatePending,
    locked,
    isWinner,
    userShareD6,
    userSharePct,
    payoutEstimates,
    payoutEstimatesPending,
    winningOutcomeLabel,
    claimed,
    claimInFlight: t1,
    onClaim,
    children
  } = t0;
  const claimInFlight = t1 === undefined ? false : t1;
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  let t2;
  if ($[0] !== isWinner || $[1] !== locked || $[2] !== viewerStatePending) {
    t2 = deriveStage(viewerStatePending, locked, isWinner);
    $[0] = isWinner;
    $[1] = locked;
    $[2] = viewerStatePending;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const stage = t2;
  let t3;
  if ($[4] !== amount || $[5] !== claimInFlight || $[6] !== claimed || $[7] !== helpModalOpen || $[8] !== locked || $[9] !== payoutEstimates || $[10] !== payoutEstimatesPending || $[11] !== stage || $[12] !== userShareD6 || $[13] !== userSharePct || $[14] !== winningOutcomeLabel) {
    t3 = {
      stage,
      locked,
      amount,
      userShareD6,
      userSharePct,
      payoutEstimates,
      payoutEstimatesPending,
      winningOutcomeLabel,
      claimed,
      claimInFlight,
      helpModalOpen
    };
    $[4] = amount;
    $[5] = claimInFlight;
    $[6] = claimed;
    $[7] = helpModalOpen;
    $[8] = locked;
    $[9] = payoutEstimates;
    $[10] = payoutEstimatesPending;
    $[11] = stage;
    $[12] = userShareD6;
    $[13] = userSharePct;
    $[14] = winningOutcomeLabel;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== onClaim) {
    t4 = {
      onClaim,
      setHelpModalOpen
    };
    $[16] = onClaim;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t3 || $[19] !== t4) {
    t5 = {
      state: t3,
      actions: t4
    };
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const value = t5;
  let t6;
  if ($[21] !== children || $[22] !== value) {
    t6 = <PrizePoolContext.Provider value={value}>{children}</PrizePoolContext.Provider>;
    $[21] = children;
    $[22] = value;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}